<template>
  <div>
    <div class="tw-flex tw-flex-col">
      <div class="tw-flex tw-justify-between">
        <div
          class="tw-w-1/2 tw-flex tw-items-center tw-justify-between"
          v-if="!byRange"
        >
          <blast-select v-model="timeSlot" id="filter" class="tw-w-1/2">
            <option value="all" selected>{{$t('wi.all_dates')}}</option>
            <optgroup :label="$t('wi.month')">
              <option
                v-for="timeSlot in timeSlots"
                v-text="`${timeSlot.formatted} (${timeSlot.count})`"
                :value="timeSlot.original"
              ></option>
            </optgroup>
          </blast-select>
          <button class="btn btn-link btn-xs" @click="bindDateRange()">
            {{ $t('wi.filter_by_date_range') }}
          </button>
        </div>
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between" v-else>
          <input
            type="text"
            class="form-control transparent tw-w-1/2"
            id="filter-range"
          />
          <button class="btn btn-link btn-xs" @click="byRange = false">
            {{ $t('wi.filter_by_month') }}
          </button>
        </div>
        <div class="tw-w-1/4 tw-flex tw-items-center">
          <blast-text-input
            @input="setSearch"
            :placeholder="$t('wi.search')"
            class="tw-w-full tw-mr-4"
          />
          <button
            class="btn btn-link btn-xs"
            @click="isThumbnailView = !isThumbnailView"
          >
            <i class="fa fa-th-list"></i>
          </button>
        </div>
      </div>
      <div class="tw-flex tw-flex-wrap tw-mb-8" v-if="isThumbnailView">
        <media-card-element
          v-for="video in files.videos.data"
          :video="video"
          :key="video.id"
          @preview="handlePreview"
          @selected="handlePush"
          @deselected="handleRemove"
        />
      </div>
      <div v-else>
        <div
          class="tw-flex tw-h-16 tw-mb-4 tw-items-center tw-justify-between tw-border-b tw-p-2 tw-font-medium"
        >
          <div class="tw-w-12 tw-h-12">{{ $t('wi.title') }}</div>
          <div class="tw-w-1/4"></div>
          <div class="tw-w-1/4 tw-hidden md:tw-block">{{ $t('wi.description') }}</div>
          <div class="tw-w-32">{{ $t('wi.added_on') }}</div>
          <div class="tw-w-24 tw-hidden md:tw-block">{{ $t('wi.file_size') }}</div>
          <div class="tw-w-24 tw-text-right">{{ $t('wi.preview') }}</div>
        </div>
        <media-list-element
          v-for="video in files.videos.data"
          :video="video"
          :key="video.id"
          @preview="handlePreview"
          @selected="handlePush"
          @deselected="handleRemove"
        />
      </div>
      <div
        class="tw-flex tw-justify-between tw-items-center"
        v-if="files.videos.total"
      >
        <span class="tw-text-grey tw-font-thin">{{
          $t('wi.showing_of_total_entities_replace', {
            start: files.videos.from,
            end: files.videos.to,
            total: files.videos.total
          })
        }}</span>
        <el-pagination
          small
          layout="prev, pager, next"
          :page-size="files.videos.per_page"
          :total="files.videos.total"
          @current-change="goToPage"
          @next-click="navigateTo('next')"
          @prev-click="navigateTo('prev')"
          :current-page.sync="currentPage"
        >
        </el-pagination>
      </div>
      <div
        v-else
        class="tw-flex tw-items-center tw-justify-center tw-text-grey-dark"
      >
        <p>{{ $t('wi.no_video_in_your_library') }}</p>
      </div>
    </div>
    <el-dialog
      v-if="previewID"
      :title="getVideoById(previewID).name"
      :visible.sync="previewModalVisible"
      width="50%"
      top="10vh"
      :lock-scroll="true"
      @closed="clearPreview"
      custom-class="tw-py-0"
      append-to-body
    >
      <video controls autoplay v-if="previewID" id="video-preview">
        <source
          :src="getVideoById(previewID).video"
          type="video/mp4"
          v-if="previewID"
        />
      </video>
      <div slot="footer" class="tw-flex tw-justify-start">
        <button class="btn btn-link" @click="previewModalVisible = false">
          {{$t('wi.close')}}
        </button>
        <button
          class="btn btn-primary"
          @click="selectVideo"
          :disabled="selectedVideos.length > 10"
        >
          {{$t('wi.select')}}
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import 'element-ui/lib/theme-chalk/index.css';
  import { Dialog, Pagination } from 'element-ui';
  import MediaCardElement from './components/MediaCardElement';
  import MediaListElement from './components/MediaListElement';
  import { mapActions, mapGetters, mapMutations } from 'vuex';
  import 'animate.css';
  import { debounce } from 'lodash';
  import moment from 'moment';
  import { format } from "../../moment";

  export default {
    name: 'BlastFileManager',

    props: {
      files: { required: true },
    },

    components: {
      'el-dialog': Dialog,
      'el-pagination': Pagination,
      MediaCardElement,
      MediaListElement,
    },

    data() {
      return {
        timeSlot: '',
        search: '',
        byRange: false,
        previewModalVisible: false,
        previewID: null,
        isThumbnailView: false,
        currentPage: 1,
        startDate: null,
        endDate: null,
        slot: 'all',
      };
    },

    watch: {
      timeSlot(value) {
        this.slot = value;
        this.currentPage = 1;
        this.$emit('filterChanged', {
          type: 'slot',
          value: {
            page: 1,
            slot: value,
          },
        });
      },
    },

    computed: {
      ...mapGetters('player-profile', [
        'selectedVideos',
        'modal',
      ]),
      timeSlots() {
        return Object.keys(this.files.count).map((item) => {
          return {
            formatted: moment(item).format(format.shortDate),
            original: item,
            count: this.files.count[item]
          }
        })
      }
    },

    methods: {
      ...mapActions({
        fetchVideos: 'player-profile/fetchVideos',
      }),

      ...mapMutations({
        pushVideo: 'player-profile/pushSelectedVideo',
        removeVideo: 'player-profile/removeSelectedVideo',
        setSearchPhrase: 'player-profile/setSearchPhrase',
      }),

      setSearch: debounce(function (q) {
        this.setSearchPhrase(q);
        this.currentPage = 1;
        this.goToPage(1);
      }, 300),

      handleRemove(video) {
        this.removeVideo(video);
      },

      handlePush(video) {
        this.pushVideo(video);
      },

      handlePreview(id) {
        this.previewID = id;
        this.previewModalVisible = true;
      },

      clearPreview() {
        const videoElement = document.getElementById('video-preview');
        videoElement.pause();
        videoElement.removeAttribute('src');
        videoElement.load();
        this.previewID = null;
      },

      getVideoById(id) {
        return this.files.videos.data.find(video => video.id === id);
      },

      selectVideo() {
        if (!this.selectedVideos.includes(this.previewID)) {
          this.handlePush(this.getVideoById(this.previewID));
        }

        this.previewModalVisible = false;
      },

      goToPage(page) {
        this.$emit('filterChanged', {
          type: this.byRange ? 'range' : 'slot',
          value: {
            startDate: this.startDate
              ? this.startDate.format('YYYY-MM-DD')
              : undefined,
            endDate: this.endDate
              ? this.endDate.format('YYYY-MM-DD')
              : undefined,
            page: page,
            slot: this.slot ? this.slot : undefined,
          },
        });
      },

      navigateTo(direction) {
        const modifier = direction === 'next' ? 1 : -1;
        this.goToPage(this.files.videos.current_page + modifier);
      },

      bindDateRange(dates = null) {
        this.byRange = true;
        const dateRangeOptions = {};
        if (dates) {
          this.startDate = dateRangeOptions.startDate = moment(
            dates.start,
            'YYYY-MM-DD',
          );
          this.endDate = dateRangeOptions.endDate = moment(
            dates.end,
            'YYYY-MM-DD',
          );
        }
        dateRangeOptions.locale = {
          format: format.date,
          applyLabel: this.$t('wi.apply'),
          fromLabel: this.$t('wi.event_from'),
          toLabel: this.$t('wi.event_to'),
          customRangeLabel: this.$t('wi.custom_range'),
          cancelLabel: this.$t('wi.clear'),
          daysOfWeek: [
            this.$t('wi.sunday_shortname'),
            this.$t('wi.monday_shortname'),
            this.$t('wi.tuesday_shortname'),
            this.$t('wi.wednesday_shortname'),
            this.$t('wi.thursday_shortname'),
            this.$t('wi.friday_shortname'),
            this.$t('wi.saturday_shortname'),
          ],
          monthNames: [
            this.$t('wi.january'),
            this.$t('wi.february'),
            this.$t('wi.march'),
            this.$t('wi.april'),
            this.$t('wi.may'),
            this.$t('wi.june'),
            this.$t('wi.july'),
            this.$t('wi.august'),
            this.$t('wi.september'),
            this.$t('wi.october'),
            this.$t('wi.november'),
            this.$t('wi.december'),
          ],
          firstDay: 1,
        };
        this.$nextTick(() => {
          $('#filter-range')
            .daterangepicker(dateRangeOptions)
            .on('apply.daterangepicker', (e, { startDate, endDate }) => {
              this.startDate = startDate;
              this.endDate = endDate;
              this.currentPage = 1;

              this.$emit('filterChanged', {
                type: 'range',
                value: {
                  startDate: this.startDate.format('YYYY-MM-DD'),
                  endDate: this.endDate.format('YYYY-MM-DD'),
                  page: 1,
                },
              });
            });
        });
      },
    },

    mounted() {
      if (this.modal.hasOwnProperty('dates')
        && this.modal.dates !== undefined) {
        this.bindDateRange(this.modal.dates);
      }
    },
  };
</script>

<style scoped>
  .el-dialog__body {
    padding: 0 20px;
  }

  #video-preview {
    max-height: 70vh;
    width: 100%;
    margin: 0 auto;
    display: block;
  }
</style>
